import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { Search, ChevronUp, ChevronDown, X, Pencil, Download, Plus, User, Phone, Shield, Trash2 } from 'lucide-react';
import api from '../utils/axios';
import PatientFormDialog from './PatientFormDialog';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatPhone(phone) {
    if (!phone) return '—';
    const d = phone.replace(/\D/g, '');
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return phone;
}

function formatDob(iso) {
    if (!iso) return '—';
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US');
}

function displayGender(g) {
    return { male: 'Male', female: 'Female', other: 'Other' }[g] ?? 'Unknown';
}

function deriveSex(g) {
    return { male: 'M', female: 'F', other: 'O' }[g] ?? '—';
}

function composeName(p) {
    return [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ');
}

function deriveMrn(p) {
    if (p.mrn) return p.mrn;
    return 'MRN-' + p.patient_id.replaceAll('-', '').slice(0, 6).toUpperCase();
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

function Avatar({ name, size = 40 }) {
    const initials = name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
    return (
        <div
            aria-hidden="true"
            style={{
                width: size, height: size, borderRadius: '50%',
                background: 'var(--ab-bg-disabled)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--ab-font-sans)', fontSize: size * 0.35,
                fontWeight: 600, color: 'var(--ab-fg-muted)', flexShrink: 0,
            }}
        >
            {initials || '?'}
        </div>
    );
}

// ─── KV ─────────────────────────────────────────────────────────────────────

function KV({ k, v, full = false }) {
    return (
        <div style={{ gridColumn: full ? '1 / -1' : undefined }}>
            <div style={{
                fontSize: 11, fontWeight: 600, color: 'var(--ab-fg-subtle)',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2,
            }}>
                {k}
            </div>
            <div style={{ fontSize: 14, color: v ? 'var(--ab-fg)' : 'var(--ab-fg-subtle)' }}>
                {v || '—'}
            </div>
        </div>
    );
}

// ─── SectionHead ─────────────────────────────────────────────────────────────

function SectionHead({ icon: Icon, title }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Icon size={15} style={{ color: 'var(--ab-fg-muted)' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ab-fg-muted)' }}>{title}</span>
        </div>
    );
}

// ─── DetailCard ──────────────────────────────────────────────────────────────

function DetailCard({ children }) {
    return (
        <div style={{
            background: '#fff', border: '1px solid var(--ab-border-hair)',
            borderRadius: 8, padding: '16px 20px', marginBottom: 16,
        }}>
            {children}
        </div>
    );
}

// ─── PTh ─────────────────────────────────────────────────────────────────────

function PTh({ label, w, align = 'left', sortKey, sort, setSort }) {
    const isSorted = sort && sort.key === sortKey;
    const handleSort = () => {
        if (!sortKey) return;
        setSort(prev =>
            prev.key === sortKey
                ? { key: sortKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key: sortKey, dir: 'asc' }
        );
    };
    return (
        <th
            role="columnheader"
            aria-sort={sortKey ? (isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
            tabIndex={sortKey ? 0 : -1}
            onClick={handleSort}
            onKeyDown={e => { if (sortKey && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleSort(); } }}
            style={{
                width: w, textAlign: align, padding: '10px 12px',
                fontSize: 12, fontWeight: 600, color: 'var(--ab-fg-subtle)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                cursor: sortKey ? 'pointer' : 'default', userSelect: 'none',
                borderBottom: '1px solid var(--ab-border-hair)', whiteSpace: 'nowrap',
            }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {label}
                {sortKey && isSorted && (sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
            </span>
        </th>
    );
}

// ─── PatientRow ───────────────────────────────────────────────────────────────

function PatientRow({ row, onOpen, openTriggerRef }) {
    const [hovered, setHovered] = useState(false);
    const rowRef = useRef(null);
    const mrn = deriveMrn(row);

    const handleOpen = () => {
        openTriggerRef.current = rowRef.current;
        onOpen(row);
    };

    return (
        <tr
            ref={rowRef}
            role="button"
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); } }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                cursor: 'pointer',
                background: hovered ? '#f8fafc' : 'transparent',
                borderBottom: '1px solid var(--ab-border-hair)',
            }}
        >
            <td style={{ padding: '12px', minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--ab-fg)' }}>{composeName(row)}</div>
                <div style={{
                    fontSize: 12, color: 'var(--ab-fg-subtle)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                }}>
                    {mrn}
                </div>
            </td>
            <td style={{ padding: '12px', fontSize: 14, color: 'var(--ab-fg-muted)' }}>{formatDob(row.date_of_birth)}</td>
            <td style={{ padding: '12px', fontSize: 14, color: 'var(--ab-fg-muted)', textAlign: 'center' }}>{deriveSex(row.gender)}</td>
            <td style={{
                padding: '12px', fontSize: 14,
                color: row.primary_insurance?.insurance_company ? 'var(--ab-fg)' : 'var(--ab-fg-subtle)',
            }}>
                {row.primary_insurance?.insurance_company || '—'}
            </td>
            <td style={{
                padding: '12px', fontSize: 14,
                color: row.primary_insurance?.subscriber_member_id ? 'var(--ab-fg)' : 'var(--ab-fg-subtle)',
            }}>
                {row.primary_insurance?.subscriber_member_id || '—'}
            </td>
        </tr>
    );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────

function EmptyState() {
    return (
        <tr>
            <td colSpan={5} style={{ padding: '48px 12px', textAlign: 'center', color: 'var(--ab-fg-subtle)', fontSize: 14 }}>
                No patients match your filters.
            </td>
        </tr>
    );
}

// ─── PatientDetailPanel ───────────────────────────────────────────────────────

function PatientDetailPanel({ patient, onClose, onDelete, onEdit, deleting }) {
    const closeBtnRef = useRef(null);
    const titleId = 'patient-panel-title';
    const mrn = deriveMrn(patient);

    useEffect(() => { closeBtnRef.current?.focus(); }, []);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const addrParts = [
        patient.address_line1,
        patient.address_line2 || null,
        patient.city && patient.state
            ? `${patient.city}, ${patient.state} ${patient.zip_code || ''}`.trim()
            : null,
    ].filter(Boolean).join(', ');

    return (
        <>
            <style>{`@keyframes patient-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
            <div
                onClick={onClose}
                data-testid="panel-backdrop"
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1200, pointerEvents: 'auto' }}
            />
            <aside
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                style={{
                    position: 'fixed', top: 0, right: 0, bottom: 0,
                    width: 640, maxWidth: '95vw',
                    background: 'var(--ab-bg)',
                    zIndex: 1300,
                    display: 'flex', flexDirection: 'column',
                    animation: 'patient-slide-in 0.2s ease',
                    overflowY: 'auto',
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
                }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '20px 24px', borderBottom: '1px solid var(--ab-border-hair)',
                    flexShrink: 0,
                }}>
                    <Avatar name={composeName(patient)} size={56} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 id={titleId} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--ab-fg)', fontFamily: 'var(--ab-font-sans)' }}>
                            {composeName(patient)}
                        </h2>
                        <div style={{ fontSize: 13, color: 'var(--ab-fg-muted)', marginTop: 2 }}>
                            {mrn} · {formatDob(patient.date_of_birth)} · {deriveSex(patient.gender)}
                        </div>
                    </div>
                    <button
                        onClick={onEdit}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '6px 14px', border: '1px solid var(--ab-border-hair)',
                            borderRadius: 6, background: '#fff', cursor: 'pointer',
                            fontSize: 13, fontWeight: 500, color: 'var(--ab-fg)',
                            fontFamily: 'var(--ab-font-sans)',
                        }}
                    >
                        <Pencil size={14} /> Edit
                    </button>
                    <button
                        aria-label="Delete patient"
                        disabled={deleting}
                        onClick={onDelete}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '6px 14px', border: '1px solid #fecaca',
                            borderRadius: 6, background: '#fff', cursor: deleting ? 'wait' : 'pointer',
                            fontSize: 13, fontWeight: 500, color: '#b91c1c',
                            fontFamily: 'var(--ab-font-sans)',
                        }}
                    >
                        <Trash2 size={14} /> Delete
                    </button>
                    <button
                        ref={closeBtnRef}
                        aria-label="Close panel"
                        onClick={onClose}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, border: 'none', background: 'transparent',
                            cursor: 'pointer', borderRadius: 4, color: 'var(--ab-fg-muted)',
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                    <DetailCard>
                        <SectionHead icon={User} title="Demographics" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 20px' }}>
                            <KV k="Full Name" v={composeName(patient)} full />
                            <KV k="Date of Birth" v={formatDob(patient.date_of_birth)} />
                            <KV k="Sex" v={displayGender(patient.gender)} />
                            <KV k="MRN" v={mrn} />
                        </div>
                    </DetailCard>

                    <DetailCard>
                        <SectionHead icon={Phone} title="Contact" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 20px' }}>
                            <KV k="Phone" v={formatPhone(patient.phone)} />
                            <KV k="Email" v={patient.email} />
                            <KV k="Address" v={addrParts} full />
                        </div>
                    </DetailCard>

                    <DetailCard>
                        <SectionHead icon={Shield} title="Insurance" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 20px' }}>
                            <KV k="Primary Payer" v={patient.primary_insurance?.insurance_company} />
                            <KV k="Member ID" v={patient.primary_insurance?.subscriber_member_id} />
                            <KV k="Group ID" v={patient.primary_insurance?.group_number} />
                        </div>
                    </DetailCard>
                </div>
            </aside>
        </>
    );
}

// ─── PatientsManagement ───────────────────────────────────────────────────────

const PatientsManagement = () => {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [search, setSearch] = useState('');
    const [payerFilter, setPayerFilter] = useState('All payers');
    const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
    const [openPatient, setOpenPatient] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deletingPatient, setDeletingPatient] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const openTriggerRef = useRef(null);

    const loadPatients = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/patients/');
            const data = response.data.data || response.data.results || response.data;
            setPatients(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Failed to load patients: ' + (err.response?.data?.detail || err.message));
            setPatients([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadPatients(); }, [loadPatients]);

    const payerOptions = useMemo(() => {
        const payers = patients
            .map(p => p.primary_insurance?.insurance_company)
            .filter(Boolean);
        return ['All payers', ...[...new Set(payers)].sort()];
    }, [patients]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        let list = patients.filter(p => {
            if (payerFilter !== 'All payers') {
                if (p.primary_insurance?.insurance_company !== payerFilter) return false;
            }
            if (!q) return true;
            const name = composeName(p).toLowerCase();
            const mrn = deriveMrn(p).toLowerCase();
            return (
                name.includes(q) ||
                mrn.includes(q) ||
                (p.primary_insurance?.subscriber_member_id || '').toLowerCase().includes(q) ||
                (p.primary_insurance?.policy_number || '').toLowerCase().includes(q) ||
                (p.primary_insurance?.group_number || '').toLowerCase().includes(q) ||
                (p.primary_insurance?.insurance_company || '').toLowerCase().includes(q) ||
                (p.email || '').toLowerCase().includes(q)
            );
        });

        list = [...list].sort((a, b) => {
            const dir = sort.dir === 'asc' ? 1 : -1;
            switch (sort.key) {
                case 'name': return dir * composeName(a).localeCompare(composeName(b));
                case 'dob': return dir * (a.date_of_birth || '').localeCompare(b.date_of_birth || '');
                case 'sex': return dir * deriveSex(a.gender).localeCompare(deriveSex(b.gender));
                case 'payer': return dir * (a.primary_insurance?.insurance_company || '').localeCompare(b.primary_insurance?.insurance_company || '');
                case 'memberId': return dir * (a.primary_insurance?.subscriber_member_id || '').localeCompare(b.primary_insurance?.subscriber_member_id || '');
                default: return 0;
            }
        });

        return list;
    }, [patients, search, payerFilter, sort]);

    const closePanel = () => {
        setOpenPatient(null);
        openTriggerRef.current?.focus();
    };

    const handleDeletePatient = async () => {
        if (!openPatient) return;
        const name = composeName(openPatient) || openPatient.patient_id;
        if (!window.confirm(`Delete patient ${name}? This cannot be undone.`)) return;
        setDeletingPatient(true);
        try {
            await api.delete(`/patients/${openPatient.patient_id}/`);
            setPatients(current => current.filter(p => p.patient_id !== openPatient.patient_id));
            setOpenPatient(null);
            setSuccess('Patient deleted successfully!');
        } catch (err) {
            setError('Failed to delete patient: ' + (err.response?.data?.detail || err.message));
        } finally {
            setDeletingPatient(false);
        }
    };

    return (
        <div style={{ padding: '36px 47px 80px', fontFamily: 'var(--ab-font-sans)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ab-fg)' }}>Patients</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ab-fg-muted)' }}>
                        Directory of all patients in the system
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={() => { /* TODO: CSV export */ }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '7px 14px', border: '1px solid var(--ab-border-hair)',
                            borderRadius: 6, background: '#fff', cursor: 'pointer',
                            fontSize: 13, fontWeight: 500, color: 'var(--ab-fg)',
                            fontFamily: 'var(--ab-font-sans)',
                        }}
                    >
                        <Download size={14} /> Export
                    </button>
                    <button
                        onClick={() => setCreateDialogOpen(true)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '7px 14px', border: 'none', borderRadius: 6,
                            background: 'var(--ab-fg)', cursor: 'pointer',
                            fontSize: 13, fontWeight: 500, color: 'var(--ab-fg-inverse)',
                            fontFamily: 'var(--ab-font-sans)',
                        }}
                    >
                        <Plus size={14} /> Add Patient
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ position: 'relative', maxWidth: 420, flex: '0 1 420px' }}>
                    <label htmlFor="patient-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
                        Search patients
                    </label>
                    <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ab-fg-subtle)', pointerEvents: 'none' }} />
                    <input
                        id="patient-search"
                        type="text"
                        placeholder="Search by name, MRN, payer, email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            width: '100%', paddingLeft: 34, paddingRight: 10, height: 36,
                            border: '1px solid var(--ab-border-hair)', borderRadius: 6,
                            fontSize: 13, fontFamily: 'var(--ab-font-sans)', color: 'var(--ab-fg)',
                            background: '#fff', outline: 'none', boxSizing: 'border-box',
                        }}
                    />
                </div>
                <select
                    value={payerFilter}
                    onChange={e => setPayerFilter(e.target.value)}
                    aria-label="Filter by payer"
                    style={{
                        height: 36, padding: '0 10px', border: '1px solid var(--ab-border-hair)',
                        borderRadius: 6, fontSize: 13, fontFamily: 'var(--ab-font-sans)',
                        color: 'var(--ab-fg)', background: '#fff', cursor: 'pointer',
                    }}
                >
                    {payerOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <div style={{ flex: 1 }} />
                {!loading && (
                    <span style={{ fontSize: 13, color: 'var(--ab-fg-muted)', whiteSpace: 'nowrap' }}>
                        {filtered.length} of {patients.length} patients
                    </span>
                )}
            </div>

            {loading && (
                <div style={{ height: 3, background: 'var(--ab-border-hair)', borderRadius: 2, marginBottom: 8, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: '40%', background: 'var(--ab-fg)' }} />
                </div>
            )}
            <div style={{ border: '1px solid var(--ab-border-hair)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            <PTh label="Name" w="28%" sortKey="name" sort={sort} setSort={setSort} />
                            <PTh label="DOB" w="14%" sortKey="dob" sort={sort} setSort={setSort} />
                            <PTh label="Sex" w="8%" align="center" sortKey="sex" sort={sort} setSort={setSort} />
                            <PTh label="Payer" w="28%" sortKey="payer" sort={sort} setSort={setSort} />
                            <PTh label="Member ID" w="22%" sortKey="memberId" sort={sort} setSort={setSort} />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0
                            ? <EmptyState />
                            : filtered.map(p => (
                                <PatientRow
                                    key={p.patient_id}
                                    row={p}
                                    onOpen={setOpenPatient}
                                    openTriggerRef={openTriggerRef}
                                />
                            ))
                        }
                    </tbody>
                </table>
            </div>

            {openPatient && (
                <PatientDetailPanel
                    patient={openPatient}
                    onClose={closePanel}
                    onDelete={handleDeletePatient}
                    onEdit={() => {
                        setSelectedPatient(openPatient);
                        setEditDialogOpen(true);
                    }}
                    deleting={deletingPatient}
                />
            )}

            <PatientFormDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onPatientCreated={async () => {
                    setCreateDialogOpen(false);
                    setSuccess('Patient created successfully!');
                    await loadPatients();
                }}
                loading={loading}
                onLoadingChange={setLoading}
            />

            <PatientFormDialog
                open={editDialogOpen}
                patient={selectedPatient}
                onClose={() => { setEditDialogOpen(false); setSelectedPatient(null); }}
                onPatientCreated={async (saved) => {
                    setEditDialogOpen(false);
                    setSuccess('Patient updated successfully!');
                    const fresh = await api.get('/patients/').then(r => r.data.data || r.data.results || r.data);
                    if (Array.isArray(fresh)) {
                        setPatients(fresh);
                        const updated = fresh.find(p => p.patient_id === saved.patient_id);
                        if (updated && openPatient) setOpenPatient(updated);
                    }
                }}
                loading={loading}
                onLoadingChange={setLoading}
            />

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert onClose={() => setError('')} severity="error">{error}</Alert>
            </Snackbar>
            <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
                <Alert onClose={() => setSuccess('')} severity="success">{success}</Alert>
            </Snackbar>
        </div>
    );
};

export default PatientsManagement;
